import * as React from 'react';
import { truncate } from 'lodash';

import ChannelMetrics from './channel-metrics/ChannelMetrics';
import Sidenote from 'components/sidenote/Sidenote';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import ActionButton from 'components/buttons/action-button/ActionButton';
import InstagramDataEnabledBadge from 'components/instagram-data-enabled/badge/InstagramDataEnabledBadge';

import { t } from 'utils/language';
import { getExternalUrl } from 'utils/url';
import {
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  getUserNameFromChannel
} from 'utils/channels';

const USER_NAME_LENGTH = 50;

import type { Channel, IndustryBenchmark } from 'types';

type Props = {
  channel: Channel,
  showFallback: boolean,
  benchmarksResource: IndustryBenchmark,
  isFreemium: boolean | null | undefined,
  hasActiveFbConnection: boolean,
  hasRecentPosts: boolean
};

class ChannelHeader extends React.PureComponent<Props> {
  renderChannelType(channel: Channel): React.ReactNode {
    return (
      <div className="channel-header__type">
        <div className="channel-header__icon">
          <SVGDynamicIcon
            name={`${channel.type}-custom-fg`}
            width={32}
            height={32}
          />
        </div>
        <div>
          {t(`channel_label_${channel.type}`)}
        </div>
      </div>
    );
  }

  renderUserName(channel: Channel): React.ReactNode {
    const atChar = (channel.type === 'instagram' || channel.type === CHANNEL_TYPE_TIKTOK) ? '@' : '';
    const userName = getUserNameFromChannel(channel);

    return (
      <ActionButton
        key={`${channel.type}-${userName || Math.random()}`}
        link={getExternalUrl(channel.url || channel.urlRaw)} // `urlRaw` = tiktok default url, not custom FE version
      >
        {
          userName ?
            truncate(`${atChar}${userName}`, { length: USER_NAME_LENGTH }) :
            t('channel_view_profile_button')
        }
      </ActionButton>
    );
  }

  renderMetrics(showFallback: boolean, channel: Channel): React.ReactNode | undefined | null {
    const { benchmarksResource, isFreemium, hasRecentPosts } = this.props;

    if (!channel.id) {
      return null;
    }

    const channelType = channel.type;

    if (channelType !== 'instagram' && channelType !== 'youtube' && channelType !== 'tiktok') {
      return null; // we don't support other types now
    }

    return showFallback ?
      <Sidenote
        type="info"
        title={t('progress_gathering_data')}
        message={t('progress_gathering_data_description')}
        className="influencer-channel-card__gathering-sidenote"
      /> :
      <ChannelMetrics
        hasRecentPosts={hasRecentPosts}
        channelType={channelType}
        metrics={channel.metrics || {}}
        benchmarksResource={benchmarksResource}
        isFreemium={isFreemium}
      />;
  }

  render(): React.ReactNode {
    const { channel, showFallback, hasActiveFbConnection } = this.props;

    return (
      <div className="channel-header">
        <div className="channel-header__basic-info">
          {this.renderChannelType(channel)}
          {this.renderUserName(channel)}
          {channel.type === CHANNEL_TYPE_INSTAGRAM ? (
            <div className="channel-header__ig-connection-bage">
              <InstagramDataEnabledBadge
                hasActiveFbConnection={hasActiveFbConnection}
              />
            </div>
          ) : null}
        </div>

        {this.renderMetrics(showFallback, channel)}
      </div>
    );
  }
}

export default ChannelHeader;
